import {
	EventSubscriber,
	EventSubscriptionOptions,
	EventType,
	PublicKey,
} from '@drift-labs/sdk';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { dlog } from '../dev';
import Env, {
	SubscriberType,
} from '../environmentVariables/EnvironmentVariables';
import useCurrentSettings from '../hooks/useCurrentSettings';
import useCurrentWalletAdapter from '../hooks/useCurrentWalletAdapter';
import useUserAccountPubKey from '../hooks/useUserAccountPubKey';
import useWalletIsConnected from '../hooks/useWalletIsConnected';

/**
 * This provider is responsible for providing the drift blocking event subscribers to the rest of the app. It does NOT manage the actual subscriptions.
 */

const WEBSOCKET_RESUBSCRIBE_TIMEOUT_MS = 2_000;
const WEBSOCKET_ALLOWED_RETRY_ATTEMPTS = 3;

const USER_EVENTS_TO_LISTEN_TO: EventType[] = [
	'DepositRecord',
	'FundingPaymentRecord',
	'LiquidationRecord',
	'OrderRecord',
	'SettlePnlRecord',
	'SwapRecord',
	'OrderActionRecord',
];

const INITIAL_CONTEXT_VALUE: {
	ready: boolean;
	userEventSubscriber: EventSubscriber;
	globalOrderActionEventSubscriber: EventSubscriber;
} = {
	ready: false,
	userEventSubscriber: undefined,
	globalOrderActionEventSubscriber: undefined,
};

/**
 * userEventSubscriber: EventSubscriber for the current user's events
 * globalOrderActionEventSubscriber: EventSubscriber for market-wide order action events
 */
export const DriftBlockchainEventSubscriberContext = React.createContext<{
	ready: boolean;
	userEventSubscriber?: EventSubscriber;
	globalOrderActionEventSubscriber?: EventSubscriber;
}>(INITIAL_CONTEXT_VALUE);

const getWebsocketSubscriberProps = (
	address?: PublicKey,
	pollingMultiplier = 1,
	eventTypes?: EventType[]
): EventSubscriptionOptions => {
	return {
		maxTx: 128,
		maxEventsPerType: 256,
		orderBy: 'blockchain',
		commitment: 'confirmed',
		logProviderConfig: {
			type: 'websocket',
			resubTimeoutMs: WEBSOCKET_RESUBSCRIBE_TIMEOUT_MS,
			maxReconnectAttempts: WEBSOCKET_ALLOWED_RETRY_ATTEMPTS,
			fallbackFrequency: Env.pollingFrequencyMs * pollingMultiplier, // The websocket provider falls back to polling when it detects issues
		},
		address,
		eventTypes,
	};
};

const getPollingSubscriberProps = (
	address?: PublicKey,
	pollingMultiplier = 1,
	eventTypes?: EventType[]
): EventSubscriptionOptions => {
	return {
		maxTx: 128,
		maxEventsPerType: 256,
		orderBy: 'blockchain',
		commitment: 'confirmed',
		logProviderConfig: {
			type: 'polling',
			frequency: Env.pollingFrequencyMs * pollingMultiplier,
		},
		address,
		eventTypes,
	};
};

const DriftBlockchainEventSubscriberProvider = (
	props: PropsWithChildren<any>
) => {
	const connection = useDriftStore((s) => s.connection.current);
	const selectedRpc = useDriftStore((s) => s.connection.rpc);

	const driftClient = useDriftStore((s) => s.driftClient.client);
	const currentAuthority = useCurrentWalletAdapter().publicKey;
	const currentUserAccount = useUserAccountPubKey();
	const walletIsConnected = useWalletIsConnected();
	const pollingMultiplier = useDriftStore((s) => s.pollingMultiplier);

	const [contextValue, setContextValue] = useState(INITIAL_CONTEXT_VALUE);

	const eventSubscriberSetting = useCurrentSettings()[0].eventSubscriberType;

	const createSelfEventSubscriber = (
		eventSubscriberSetting: SubscriberType,
		address: PublicKey
	) => {
		dlog(`optimised_event_subscriptions`, `creating_user_event_subscriber`);

		dlog(
			`v2_auctions`,
			`creating_user_event_subscriber : ${address?.toString()}`
		);

		const useWebsocket =
			eventSubscriberSetting === 'websocket' && selectedRpc?.wsValue;

		let eventSubscriber: EventSubscriber;

		if (useWebsocket) {
			eventSubscriber = new EventSubscriber(
				connection,
				driftClient.program,
				getWebsocketSubscriberProps(
					address,
					pollingMultiplier,
					USER_EVENTS_TO_LISTEN_TO
				)
			);
		} else {
			eventSubscriber = new EventSubscriber(
				connection,
				driftClient.program,
				getPollingSubscriberProps(
					address,
					pollingMultiplier,
					USER_EVENTS_TO_LISTEN_TO
				)
			);
		}

		eventSubscriber.subscribe();

		return eventSubscriber;
	};

	const createGlobalOrderActionRecordEventSubscriber = (
		eventSubscriberSetting: SubscriberType
	) => {
		const useWebsocket =
			eventSubscriberSetting === 'websocket' && selectedRpc?.wsValue;

		let eventSubscriber: EventSubscriber;

		if (useWebsocket) {
			dlog(
				`optimised_event_subscriptions`,
				`creating_websocket_global_event_subscriber`
			);
			eventSubscriber = new EventSubscriber(
				connection,
				driftClient.program,
				getWebsocketSubscriberProps(undefined, pollingMultiplier, [
					'OrderActionRecord',
				])
			);
		} else {
			dlog(
				`optimised_event_subscriptions`,
				`creating_blockchain_global_event_subscriber`
			);
			eventSubscriber = new EventSubscriber(
				connection,
				driftClient.program,
				getPollingSubscriberProps(undefined, pollingMultiplier, [
					'OrderActionRecord',
				])
			);
		}

		eventSubscriber.subscribe();

		return eventSubscriber;
	};

	// The params that subscribing depend on can change in bursts, but we don't want to subscribe+unsubscribe in quick bursts because it's unnecessary. For this reason we're going to make a ticker to sync with these params, and only update it with a debounced hook.
	const [subscribeUpdateTicker, setSubscribeUpdateTicker] = useState([]);

	// The debounced hook which can only update the ticker at a maximum speed
	useDebounce(
		() => {
			setSubscribeUpdateTicker([]);
		},
		1000,
		[
			connection,
			driftClient,
			walletIsConnected && currentAuthority, // To prevent wasteful (un)subscribes , we only actually need to update the subscriber if the combination of these changes
			eventSubscriberSetting,
			pollingMultiplier,
			currentUserAccount,
		]
	);

	useEffect(() => {
		if (!connection) return;
		if (!driftClient) return;
		if (!driftClient.isSubscribed) return;

		const selfEventSubscriber = currentUserAccount
			? createSelfEventSubscriber(eventSubscriberSetting, currentUserAccount)
			: undefined;

		const globalOrderActionRecordEventSubscriber =
			createGlobalOrderActionRecordEventSubscriber(eventSubscriberSetting);

		setContextValue({
			ready: true,
			userEventSubscriber: selfEventSubscriber,
			globalOrderActionEventSubscriber: globalOrderActionRecordEventSubscriber,
		});

		return () => {
			if (selfEventSubscriber) selfEventSubscriber.unsubscribe();
			globalOrderActionRecordEventSubscriber.unsubscribe();
		};
	}, [subscribeUpdateTicker, eventSubscriberSetting]);

	return (
		<DriftBlockchainEventSubscriberContext.Provider value={contextValue}>
			{props.children}
		</DriftBlockchainEventSubscriberContext.Provider>
	);
};

export default DriftBlockchainEventSubscriberProvider;
